import React, { type FC, useEffect, useState, useRef, useCallback } from "react";
import type { CategoryType, PackType } from "../../../../../../../../../common-deprecated/types/CommonTypes";
import {
    getCategoryGroupIcon,
    getCategoryKey,
    getCategoryNameForCollections,
    sortCategories,
} from "../../../../../../utils/packUtils";
import Text from "../../../../../../../shared-components/css-modules/toyota/atoms/Text";
import Icon from "../../../../../../../shared-components/css-modules/toyota/atoms/Icon";
import Collapse from "../../../../../../../shared-components/css-modules/toyota/molecules/Collapse";
import styles from "./PacksAndExtras.module.scss";
import searchStyles from "../SearchBar.module.scss";
import { Pack } from "./Pack";
import { CarConfigLabel } from "../../../../../../utils/labelUtils";
import { trackOptionsToggleCategory } from "../../../../../../utils/tracking";
import { useAphPacks } from "../../../../../../hooks/useAphPacks";
import { useCarConfigDispatch, useCarConfigSelector } from "../../../../../../redux/store";
import { clearPackAddedByExtra } from "../../../../../../redux/slices/CarConfigNavigationSlice";

export const LcvPacks: FC<{ packs: PackType[]; enabledCategoryFilters: CategoryType[] }> = ({
    packs,
    enabledCategoryFilters,
}) => {
    const dispatch = useCarConfigDispatch();
    const categorizedPacks = useAphPacks(packs, enabledCategoryFilters).categorized;
    const [open, setOpen] = useState<string>(categorizedPacks.length ? getCategoryKey(categorizedPacks[0][0]) : "");
    const packAddedByExtra = useCarConfigSelector((state) => state.carConfigNavigation.packAddedByExtra);

    // Track what we need to scroll to after collapses open
    const scrollTarget = useRef<{ packId: string; rootKey: string; categoryKey: string } | null>(null);

    // Create refs map for all packs
    const packRefs = React.useRef<Map<string, HTMLElement>>(new Map());

    const setPackRef = (packId: string) => (element: HTMLLIElement | null) => {
        if (element) {
            packRefs.current.set(packId, element);
        } else {
            packRefs.current.delete(packId);
        }
    };

    const scrollToPack = useCallback((packId: string) => {
        const packElement = packRefs.current.get(packId);
        if (packElement) {
            packElement.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
        }
    }, []);

    useEffect(() => {
        if (scrollTarget.current) {
            const { packId, rootKey, categoryKey } = scrollTarget.current;
            const expectedOpen = `${rootKey}|${categoryKey}`;

            // Check if both collapses are now open
            if (open === expectedOpen) {
                // Use double requestAnimationFrame to ensure DOM is updated
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        scrollToPack(packId);
                        scrollTarget.current = null;
                    });
                });
            }
        }
    }, [open, scrollToPack]);

    useEffect(() => {
        if (categorizedPacks.length) {
            const firstSubCategoryKey = getCategoryKey(Object.entries(categorizedPacks[0][1])[0][0]);
            if (firstSubCategoryKey) {
                setOpen(`${getCategoryKey(categorizedPacks[0][0])}|${getCategoryKey(firstSubCategoryKey)}`);
            }
        }
        // Execute once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (packAddedByExtra) {
            dispatch(clearPackAddedByExtra());

            categorizedPacks.some(([rootCategory, categories]) => {
                const rootKey = getCategoryKey(rootCategory);

                return Object.entries(categories).some(([category, items]) => {
                    const categoryKey = getCategoryKey(category);
                    const packIndex = items.findIndex((item) => item.id === packAddedByExtra.id);

                    if (packIndex !== -1) {
                        const targetKey = `${rootKey}|${categoryKey}`;

                        // Set what we want to scroll to
                        scrollTarget.current = {
                            packId: packAddedByExtra.id,
                            rootKey,
                            categoryKey,
                        };

                        // If already fully open, scroll immediately
                        if (open === targetKey) {
                            requestAnimationFrame(() => {
                                requestAnimationFrame(() => {
                                    scrollToPack(packAddedByExtra.id);
                                    scrollTarget.current = null;
                                });
                            });
                        } else {
                            // Open the collapses, scroll will happen in useEffect above
                            setOpen(targetKey);
                        }

                        return true;
                    }

                    return false;
                });
            });
        }
    }, [packAddedByExtra, open, scrollToPack]);

    return categorizedPacks.length ? (
        <div className={styles["info-section-wrapper"]}>
            {categorizedPacks.map(([rootCategory, categories]) => {
                const categoryGroupIcon = getCategoryGroupIcon(rootCategory);
                const rootCategoryName = getCategoryNameForCollections(rootCategory);
                const rootKey = getCategoryKey(rootCategory);
                const sortedCategories = Object.entries(categories).sort(sortCategories);
                const firstCategoryKey = getCategoryKey(sortedCategories[0][0] || rootCategory);
                return (
                    <Collapse
                        key={`extras-${rootCategory}`}
                        className={styles["category-group"]}
                        open={open.includes(rootKey)}
                        onChange={(isOpen) => {
                            trackOptionsToggleCategory(rootCategoryName || rootKey, !!isOpen);
                            setOpen(isOpen ? firstCategoryKey : "");
                        }}
                        summary={
                            <header className={styles["category-header"]}>
                                {categoryGroupIcon && (
                                    <Icon sizing="sm" variant={categoryGroupIcon} aria-hidden="true" />
                                )}
                                <Text tag="h3" variant="apheleia-h4">
                                    {rootCategoryName}
                                </Text>
                            </header>
                        }
                    >
                        {sortedCategories.map(([category, items]) => {
                            const categoryKey = getCategoryKey(category);
                            const subCategoryName = getCategoryNameForCollections(category);
                            return (
                                <div
                                    className={styles["features-list-item"]}
                                    key={`extras-${rootCategory}-${category}`}
                                >
                                    <Collapse
                                        summary={subCategoryName}
                                        open={open === `${rootKey}|${categoryKey}`}
                                        onChange={(isOpen) => {
                                            trackOptionsToggleCategory(
                                                subCategoryName || `${rootKey}|${categoryKey}`,
                                                !!isOpen,
                                            );
                                            setOpen(isOpen ? `${rootKey}|${categoryKey}` : rootKey);
                                        }}
                                    >
                                        <ul role="list" className={styles["collapse-list"]}>
                                            {items.map((item, index) => (
                                                <Pack
                                                    ref={setPackRef(item.id)}
                                                    pack={item}
                                                    key={`extras-${rootCategory}-${category}-${item.id}`}
                                                    firstShownPack={index === 0}
                                                />
                                            ))}
                                        </ul>
                                    </Collapse>
                                </div>
                            );
                        })}
                    </Collapse>
                );
            })}
        </div>
    ) : (
        <div className={searchStyles.notfound} aria-live="assertive" aria-atomic="true">
            <CarConfigLabel label="carConfigPacksNotFound" />
        </div>
    );
};
